<template>
  <div>
    <div class="grid-9">
      <div v-if="isAdminOrProductBuilder()" class="grid-9 span-9 grid-column-gap-28 mb-40">
        <div class="span-3 span-12-md mb-20-md">
          <brand-selector
            :selected-name="selectedBrand ? selectedBrand.name : null"
            :placeholder="'Filter by Brand'"
            :on-select="brandChangeCallback"
            :on-brands-fetched="brandsFetched"
          />
        </div>
      </div>
    </div>
    <div class="grid-9 grid-column-gap-0">
      <div class="sidebar-wrapper span-2">
        <slot />
      </div>
      <div class="dashboard-table span-7 span-9-sm">
        <table style="table-layout: auto" class="table-main scroll-table-lg">
          <thead class="bg-light-grey">
            <tr>
              <th>Image</th>
              <th>Title</th>
              <th>Product Type</th>
              <th>Lead Time</th>
              <th>unit price ($)</th>
              <th>wholesale price</th>
              <th>variant added?</th>
              <th>
                actions
                <bulletin-button
                  size="xxsm"
                  :disabled="products.length === 0 || !enableAddAll"
                  @click="handleAddAllClick"
                >
                  add all
                </bulletin-button>
              </th>
            </tr>
          </thead>
          <tbody>
            <loading-bar @upload-complete="getPageOfDrafts(1)" />
            <template v-for="product in products">
              <draft-product
                :key="product.id"
                :data="product"
                :product-types="productTypesString"
                :shopify-connected="$route.query.shopifyConnected"
                @is-valid="handleIsValid"
                @remove-product="removeProductFromValidSet"
                @add-product="refreshDraftList"
              />
            </template>
          </tbody>
        </table>
        <div v-if="products.length === 0" class="flex align-center justify-center my-50">no drafts for review</div>
      </div>
    </div>
    <div
      v-if="pagination && pagination.totalCount > pagination.limit"
      class="flex justify-flex-end mt-40 mb-80 mt-20-md mb-0-md"
    >
      <pagination
        :active="pagination.page"
        :limit="pagination.limit"
        :on-click="getPageOfDrafts"
        :total="pagination.totalCount"
        class="no-padding-right"
      />
    </div>
  </div>
</template>

<script>
import DraftProduct from "@/views/dashboard/drafts/DraftProduct";
import LoadingBar from "./loading-bar.vue";
import DraftApi from "@/services/api/draftApi";
import { mapGetters } from "vuex";
import { paramsHelper } from "../../../components/mixin/paramsHelper";

const DraftApiService = new DraftApi();

export default {
  name: "Drafts",
  components: {
    DraftProduct,
    LoadingBar,
  },
  mixins: [paramsHelper],
  data() {
    return {
      products: [],
      validProducts: new Set(),
      productTypes: null,
      productTypesString: "",
      pagination: {
        limit: 33,
        page: 1,
      },
      showLoader: true,
      loaderTimeout: 5000,
      enableAddAll: true,
      selectedBrand: null,
      isBrandsFetched: false,
    };
  },
  computed: {
    ...mapGetters({
      user: "userState",
    }),
  },
  async mounted() {
    await this.populateCategories();
    await this.getPageOfDrafts(this.pagination.page);
    if (this.products) {
      this.$store.commit("draftUploadEnded");
    }
  },
  beforeMount() {
    if (this.$route.query.shopifyConnected) {
      this.$store.commit("draftUploadStart");
      let message =
        "We're importing your catalog from Shopify now. It may take some time to complete so we'll email you when we're finished.";
      if (this.$route.query.noProducts) {
        message =
          "It looks like your Shopify store doesn’t have any active products. Any new products created in Shopify will now appear in Bulletin for you to review before adding them to the marketplace.";
      }
      this.$swal("Shopify Connected", message);
    }
    this.parseURLParams();
  },
  async beforeRouteLeave(to, from, next) {
    const draftsToAdd = this.$children.filter((component) => component.data !== undefined),
      hasChanges = draftsToAdd.filter((draft) => draft.$data.userEditedProduct).length !== 0;
    if (hasChanges) {
      this.$swal({
        title: "ARE YOU SURE?",
        text: "If you leave this page your changes won't be saved",
        showCancelButton: true,
        cancelButtonText: "Cancel",
        confirmButtonText: "Confirm",
      }).then(function (confirm) {
        if (confirm.value) {
          next();
        }
      });
    } else {
      next();
    }
  },
  methods: {
    transformDraftData: function (drafts) {
      return drafts.map((draft) => {
        // TODO delete transforming category_id or type into object before merge
        const categories =
          draft.categories ||
          (draft.category_id || draft.type
            ? [
                {
                  id: draft.category_id || draft.type,
                },
              ]
            : null);

        return {
          id: draft.id,
          image: draft.image,
          title: draft.title,
          categories: this.$utils.mapCategoriesToProductTypeList(categories, this.productTypes),
          leadTime: draft.lead_time,
          msrp: draft.pos_price,
          wsp: draft.wholesale_price,
          validVariant: draft.variant_added,
        };
      });
    },
    populateCategories: async function () {
      this.productTypes = (await this.$utils.getProductCategories()).map((container) => {
        return {
          ...container,
          view_components: container.children_containers.length
            ? container.children_containers.flatMap((_container) => _container.view_components)
            : container.view_components,
        };
      });
    },
    handleAddAllClick: async function () {
      const draftsToAdd = this.$children
          .filter((component) => component.data !== undefined) // filter out sidebar component
          .filter((child) => !child.$data.valid)
          .map((child) => {
            return {
              lead_time: child.product.leadTime,
              product_id: child.product.id,
              category_ids: this.getCategoryIds(child.product),
              title: child.product.title,
              unit_price: child.product.msrp,
              wholesale_price: child.product.wsp,
            };
          }),
        ids = draftsToAdd.map((draft) => draft.product_id),
        { value: yes } = await this.$swal({
          title: "Attention",
          text: "Are you sure you want to add all valid products",
          showCancelButton: true,
          cancelButtonText: "No",
          confirmButtonText: "Yes",
        });
      if (yes) {
        await DraftApiService.addDraftProducts(draftsToAdd);
        this.products = this.products.filter((product) => !ids.includes(product.id));
        await this.refreshDraftList();
        await this.$swal({
          title: "Success",
          text: "Products Added!",
        });
        this.$store.dispatch("getDraftCount");
      }
    },
    getCategoryIds: function (product) {
      return product.categories.map((item) => {
        return item.entity_id;
      });
    },
    getPageOfDrafts: async function (page) {
      if (this.isAdminOrProductBuilder() && !this.selectedBrand) {
        return;
      }

      const offset = page - 1,
        { data } = await DraftApiService.getDraftsByBrandId(
          this.selectedBrand ? this.selectedBrand.id : this.user.brand_id,
          offset
        );

      this.productTypesString = JSON.stringify(this.productTypes);
      this.products = null;
      this.products = this.transformDraftData(data.draft_products);
      this.pagination.page = page;
      this.pagination.totalCount = data.total;
      this.updateURLParams("page", this.pagination.page);
      return Promise.resolve();
    },
    refreshDraftList: async function () {
      return this.getPageOfDrafts(this.pagination.page);
    },
    handleIsValid: function (id, isValid) {
      if (isValid) {
        this.validProducts.add(id);
      } else {
        this.validProducts.delete(id);
      }
      this.enableAddAll = this.validProducts.size > 1;
    },
    removeProductFromValidSet: function (id) {
      this.validProducts.delete(id);
      this.enableAddAll = this.validProducts.size > 1;
    },
    isAdminOrProductBuilder: function () {
      return this.user.role === "ADMIN" || this.user.role === "PRODUCT_BUILDER";
    },
    brandChangeCallback: function (value) {
      this.selectedBrand = value;
      this.updateURLParams("brandId", this.selectedBrand.id);
      this.updateURLParams("brandName", this.selectedBrand.name);
      this.getPageOfDrafts(1);
    },
    brandsFetched: function () {
      this.isBrandsFetched = true;
    },
    parseURLParams: function () {
      const { page, brandId, brandName } = this.$route.query;

      if (brandId && brandName) {
        this.selectedBrand = { id: brandId, name: brandName };
      }

      if (Number(page)) {
        this.pagination.page = Number(page);
      }
    },
  },
};
</script>
